import { AccordionSectionPresentationProps } from "@DSG/MobiusOverrides/AccordionSection/AccordionSection";
import colorPallete, { bodyFonts, headlineFonts } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    faqData = "faqData",
    expandByDefault = "expandByDefault",
}

interface FAQItem {
    fields: {
        faqTitle: string;
        faqContent: string; 
        show: boolean;
    }
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.faqData]: FAQItem[];
        [fields.expandByDefault]: boolean;
    };
}

export interface FaqListStyles {
    faqWrapper?: InjectableCss;
    faqInnerWrapper?: InjectableCss;
    faqContent?: InjectableCss;
    faqMainTitle?: TypographyPresentationProps;
    faqSectionWrapper?: InjectableCss;
    faqSection?: AccordionSectionPresentationProps;
}

export const faqListStyles: FaqListStyles = {
    faqWrapper: {
        css: css`
            max-width: 1280px;
            margin: 40px 0;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    margin-bottom: 48px;
                    margin-top: -16px;
                `,
               
            ], "min")}
        `,
    },
    faqContent: {
        css: css`
            p {
                font-size: ${bodyFonts.body2RegularLg.fontSize};
                line-height: ${bodyFonts.body2RegularLg.lineHeight};
                color: ${colorPallete.grey800};
                &:last-child {
                    margin-bottom: 0;
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                css`
                    p {
                        font-size: ${bodyFonts.body2RegularSm.fontSize};
                        line-height: ${bodyFonts.body2RegularSm.lineHeight};
                    }
                `,
                null,
                null,
            ], "max")}
        `
    },
    faqMainTitle: {
        weight: headlineFonts.headline3SemiBoldLg.fontWeight,
        css: css`
            margin-bottom: 24px;
            font-size: ${headlineFonts.headline3SemiBoldLg.fontSize};
            line-height: ${headlineFonts.headline3SemiBoldLg.lineHeight};
            color: ${colorPallete.grey800};
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                css`
                    font-size: ${headlineFonts.headline3SemiBoldSm.fontSize};
                    line-height: ${headlineFonts.headline3SemiBoldSm.lineHeight};
                `,
                null,
                null,
            ], "max")}
        `
    },
    faqSectionWrapper: {
        css: css`
            border-bottom: 1px solid ${colorPallete.grey500};
        `
    },
    faqSection: {
        mergeCss: true,
        headerProps: {
            css: css`
                button {
                    border: none;
                    background: ${colorPallete.white};
                    padding: 24px;
                    span {
                        color: ${colorPallete.grey800};
                    }
                    [class*="TransitionManager"] {
                        bottom: 0;
                        top: 0;
                    }
                    ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        css`
                            padding: 24px 0;
                        `,
                        null,
                        null,
                    ], "max")}
                }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        button {
                            [class*="IconWrapper"] {
                                svg {
                                    display: none;
                                }
                                &:before {
                                    content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M3.87156 6.49016L2.10156 8.27016L12.0016 18.1602L21.9016 8.26016L20.1316 6.49016L12.0016 14.6202L3.87156 6.49016Z' fill='%230C0C0C'/%3E%3C/svg%3E");
                                    height: 24px;
                                    width: 24px;
                                }
                            }
                            &[aria-expanded="true"] {
                                [class*="IconWrapper"] {
                                    &:before {
                                        content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M20.1284 17.5098L21.8984 15.7298L11.9984 5.83984L2.09844 15.7398L3.86844 17.5098L11.9984 9.37984L20.1284 17.5098Z' fill='%230C0C0C'/%3E%3C/svg%3E");
                                    }
                                }
                            }
                        }
                    `,
                ],
                "min",
            )}
            `
        },
        panelProps: {
            css: css`
                padding: 0 24px 40px;
                border: none;

                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        padding: 0 0 24px;
                    `,
                    null,
                    null,
                ], "max")}
            `,
        },
        titleTypographyProps: {
            size: bodyFonts.body2SemiBoldLg.fontSize,
            lineHeight: bodyFonts.body2SemiBoldLg.lineHeight,
            weight: bodyFonts.body2SemiBoldLg.fontWeight,
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        font-size: ${bodyFonts.body2SemiBoldSm.fontSize};
                        line-height: ${bodyFonts.body2SemiBoldSm.lineHeight};
                    `,
                    null,
                    null,
                ], "max")}
            `
        },
    },
};

const styles = faqListStyles;

export const FaqList: FC<OwnProps> = ({ fields }) => {

    const { faqData } = fields;
  
    return (
        <StyledWrapper {...styles.faqWrapper}> 
            <StyledWrapper className="list-wrapper" {...styles.faqInnerWrapper}>
                <StyledWrapper className="title-wrapper">
                    {fields.title && (
                        <Typography {...styles.faqMainTitle} className="list-title" as="h1" variant="h1" >
                            {fields.title}
                        </Typography>
                    )}
                </StyledWrapper>
                {faqData.length > 0 && (
                    <Accordion headingLevel={2}>
                        {faqData.map((item, index) => (
                            <>    
                                {(item.fields.faqTitle && item.fields.show) && (
                                    <StyledWrapper key={index}  {...styles.faqSectionWrapper}>
                                        <ManagedAccordionSection
                                            initialExpanded={fields.expandByDefault}
                                            {...styles.faqSection}
                                            title={item.fields.faqTitle}
                                        >
                                            {item.fields.faqContent && (
                                                <StyledWrapper {...styles.faqContent}>
                                                    {parse(item.fields.faqContent, parserOptions)}    
                                                </StyledWrapper>
                                            )}
                                        </ManagedAccordionSection>
                                    </StyledWrapper>
                                )}
                            </>
                        ))}
                    </Accordion>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};



const widgetModule: WidgetModule = {
    component: FaqList,
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.expandByDefault,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.faqData,
                editorTemplate: "ListField",
                displayName: "FAQ Questions/Answers",
                getDisplay: (item: HasFields) => {
                    const {
                        faqTitle,
                        faqContent,
                        show
                    } = item.fields;
                    if (faqTitle) {
                        return faqTitle;
                    }
                    if (faqContent) {
                        return faqContent;
                    }
                    if (show) {
                        return show;
                    }
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 2,
                fieldDefinitions: [
                    {
                        name: "faqTitle",
                        editorTemplate: "TextField",
                        displayName: "Question",
                        defaultValue: "",
                        sortOrder: 0,
                        isRequired: true,
                    },
                    {
                        name: "faqContent",
                        editorTemplate: "RichTextField",
                        displayName: "Answer",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                        expandedToolbarButtons: {
                            moreMisc: {
                                buttons: [
                                    "insertLink",
                                    "insertImage",
                                    "insertHR",
                                    "specialCharacters",
                                    "insertVideo",
                                    "insertFile",
                                ],
                                buttonsVisible: 3,
                            },
                            code: {
                                buttons: ["selectAll", "html", "help"],
                                buttonsVisible: 3,
                            },
                        },
                        sortOrder: 1,
                    },
                    {
                        name: "show",
                        displayName: 'Show Question',
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
